import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, CardActions, Button, Grid } from '@mui/material';
import {
  PersonSearch,
  Timeline,
  EventNote,
  PersonAdd,
  Business,
  SwapHoriz,
  AttachMoney,
  Today,
} from '@mui/icons-material';
import { PageHeader, StatCard } from '../../components/common/pageKit';
import { formatCurrency } from '../../utils/currency';
import crmService, { CrmSummary } from '../../services/crmService';
import LeadsPage from './LeadsPage';
import PipelinePage from './PipelinePage';
import ActivitiesPage from './ActivitiesPage';
import RevenueCommandCenter from './RevenueCommandCenter';
import CrmCustomer360Page from './CrmCustomer360Page';
import OpportunityWorkspacePage from './OpportunityWorkspacePage';
import SalesForecastPage from './SalesForecastPage';
import MyDayPage from './MyDayPage';
import TasksPage from './TasksPage';
import CrmCalendarPage from './CrmCalendarPage';
import TerritoriesPage from './TerritoriesPage';
import SalesLeaderboardPage from './SalesLeaderboardPage';
import WinLossPage from './WinLossPage';
import CampaignsPage from './CampaignsPage';
import ForecastSubmissionPage from './ForecastSubmissionPage';
import ApprovalsCenterPage from './ApprovalsCenterPage';

const crmModules = [
  {
    title: 'Leads',
    description: 'Prospects moving toward a customer record — track, qualify and convert.',
    icon: <PersonSearch />,
    path: '/crm/leads',
    color: 'primary' as const,
  },
  {
    title: 'Pipeline',
    description: 'Every open opportunity as a kanban board, grouped by stage.',
    icon: <Timeline />,
    path: '/crm/pipeline',
    color: 'secondary' as const,
  },
  {
    title: 'Activities',
    description: 'Calls, emails, meetings, notes, tasks and demos — plus what follow-ups are due.',
    icon: <EventNote />,
    path: '/crm/activities',
    color: 'info' as const,
  },
];

const CrmOverview: React.FC = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<CrmSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    crmService
      .getSummary()
      .then(setSummary)
      .catch((err) => setError(err.message || 'Failed to load CRM summary'));
  }, []);

  const fmtMoney = (n: number | null) => (n === null ? null : formatCurrency(n));
  const fmtPct = (n: number | null) => (n === null ? null : `${n}%`);

  return (
    <Box>
      <PageHeader title="CRM" subtitle="Leads, pipeline and activity — in one place" />

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 4,
        }}
      >
        <StatCard
          title="New Leads This Week"
          value={summary ? summary.newLeadsThisWeek : null}
          icon={<PersonAdd />}
          color="#0284c7"
        />
        <StatCard
          title="Qualified Leads"
          value={summary ? summary.qualifiedLeads : null}
          icon={<Business />}
          color="#7c3aed"
        />
        <StatCard
          title="Conversion Rate"
          value={summary ? fmtPct(summary.conversionRatePct) : null}
          icon={<SwapHoriz />}
          color="#059669"
        />
        <StatCard
          title="Open Opportunities"
          value={summary ? summary.openOpportunities : null}
          icon={<Timeline />}
          color="#ea580c"
        />
        <StatCard
          title="Weighted Forecast"
          value={summary ? fmtMoney(summary.weightedForecast) : null}
          icon={<AttachMoney />}
          color="#0284c7"
        />
        <StatCard
          title="Won This Month"
          value={summary ? fmtMoney(summary.wonThisMonth) : null}
          icon={<AttachMoney />}
          color="#059669"
        />
        <StatCard
          title="Lost This Month"
          value={summary ? fmtMoney(summary.lostThisMonth) : null}
          icon={<AttachMoney />}
          color="#dc2626"
        />
        <StatCard
          title="Activities This Week"
          value={summary ? summary.activitiesThisWeek : null}
          icon={<Today />}
          color="#64748b"
        />
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Modules
      </Typography>
      <Grid container spacing={3}>
        {crmModules.map((mod) => (
          <Grid key={mod.title} size={{ xs: 12, sm: 6, md: 4 }}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                transition: 'transform .18s ease, box-shadow .18s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box sx={{ color: `${mod.color}.main`, mr: 1.5, fontSize: 30, display: 'flex' }}>{mod.icon}</Box>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                    {mod.title}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {mod.description}
                </Typography>
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button size="small" color={mod.color} onClick={() => navigate(mod.path)} sx={{ fontWeight: 700 }}>
                  Open
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

const CrmPage: React.FC = () => {
  return (
    <Routes>
      <Route index element={<RevenueCommandCenter />} />
      <Route path="overview" element={<CrmOverview />} />
      <Route path="leads" element={<LeadsPage />} />
      <Route path="pipeline" element={<PipelinePage />} />
      <Route path="forecast" element={<SalesForecastPage />} />
      <Route path="activities" element={<ActivitiesPage />} />
      <Route path="my-day" element={<MyDayPage />} />
      <Route path="tasks" element={<TasksPage />} />
      <Route path="calendar" element={<CrmCalendarPage />} />
      <Route path="territories" element={<TerritoriesPage />} />
      <Route path="leaderboard" element={<SalesLeaderboardPage />} />
      <Route path="win-loss" element={<WinLossPage />} />
      <Route path="campaigns" element={<CampaignsPage />} />
      <Route path="forecast-submission" element={<ForecastSubmissionPage />} />
      <Route path="approvals" element={<ApprovalsCenterPage />} />
      <Route path="customer/:id/360" element={<CrmCustomer360Page />} />
      <Route path="opportunity/:id/workspace" element={<OpportunityWorkspacePage />} />
    </Routes>
  );
};

export default CrmPage;
